<template>
    <divider-generic
    icon="credit-card"
    dividerSize="medium"
    :applyBorder="false"
    :title="t('cards.activate.step1.cardDivider')"/>
    <!-- Card Pan Input -->
    <input-generic
    id="cardPan"
    :params="cardPanParams"
    :inputValue="formData.cardPan"
    @input="[emit('formDataChange', { field: 'cardPan', inputValue: $event }), clearFormErrors('cardPan')]"
    @onEnter="[emit('formDataChange', { field: 'cardPan', inputValue: $event }), clearFormErrors('cardPan')]"/>
    <!-- Details Container -->
    <container-collapsible
    v-if="cardValidateResponse && cardValidateResponse.valid"
    doubleColumn
    :class="{'is-mobile': isMobile}"
    :title="t('textBlocks.showDetails')"
    :openTitle="t('textBlocks.hideDetails')">
        <text-block-generic
        v-for="entry in cardInfoParsed"
        :key="entry"
        :params="entry"
        :applyEllipsis="entry.applyEllipsis"
        :mask="entry.mask"/>
    </container-collapsible>
    <!-- Card Pan Validate Button -->
    <div v-else>
        <button-tertiary
        id="cardValidated"
        :label="t('cards.activate.step1.cardPanValidateButton')"
        @clicked="formData.cardPan === '' ? showMandatoryErrorsHandler() : emit('validateCardPan')"/>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import ContainerCollapsible from '@/components/generic/containers/Collapsible'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'

/* Vue */
import { ref, watch, onMounted, computed } from 'vue'
/* Assets */
import { CARD_NUMBER, ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { amountFormat } from '@/assets/scripts/amounts'
import { cardNumberMaskedFormat } from '@/assets/scripts/strings'
import { getMonthYear } from '@/assets/scripts/dateBuilders'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['formDataChange', 'formErrorsCleared', 'resetShowMandatoryErrorsProp', 'validateCardPan', 'scrollTo'])

/* props received
@clearFormErrors { Boolean } - Prop to remove of form errors
@formData { Object } - object with info for our current form
@isMobile { Boolean }
@mandatoryList {Array} Array of mandatory keys corresponding with our form
@showMandatoryErrors {Boolean} Prop to trigger display of mandatory errors
@cardValidateResponse {Object} Response with info of card pan validation
*/
const props = defineProps({
    clearFormErrors: Boolean,
    formData: Object,
    isMobile: Boolean,
    mandatoryList: Array,
    showMandatoryErrors: Boolean,
    cardValidateResponse: Object
})

/* Data */
const cardPanParams = ref({
    labelText: t('cards.activate.step1.cardPanLabel'),
    placeholder: t('cards.activate.step1.cardPanPlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null,
    bottomMessage: t('cards.activate.step1.cardPanNote'),
    mask: CARD_NUMBER
})

watch(() => props.cardValidateResponse, newValue => {
	if (props.cardValidateResponse && !newValue.valid) {
        cardPanParams.value.errorState = true
        cardPanParams.value.errorMessage = props.cardValidateResponse.additionalErrorMessage
    } else if (props.cardValidateResponse && newValue.valid) {
        emit('formDataChange', { field: 'cardValidated', inputValue: true })
    }
})

const cardInfoParsed = computed(() => {
    const cardInfo = props.cardValidateResponse.card

    return [{
		label: t('cards.activate.step1.balance'),
		content: !cardInfo.balance && cardInfo.balance !== 0 ? '-' : amountFormat(cardInfo.balance, cardInfo.currency)
	}, {
		label: t('cards.activate.step1.accountingBalance'),
		content: !cardInfo.accountingBalance && cardInfo.accountingBalance !== 0 ? '-' : amountFormat(cardInfo.accountingBalance, cardInfo.currency)
	}, {
		label: t('cards.activate.step1.account'),
		content: cardInfo.accountNumber,
		mask: ACCOUNT_NUMBER
	}, {
		label: t('cards.activate.step1.name'),
		content: cardInfo.name
	}, {
		label: t('cards.activate.step1.cardNumber'),
        content: cardNumberMaskedFormat(cardInfo.cardNumber)
	}, {
		label: t('cards.activate.step1.type'),
		content: cardInfo.cardTypeDescription || cardInfo.type
	}, {
		label: t('cards.activate.step1.situation'),
		content: cardInfo.statusDescriptive
	}, {
		label: t('cards.activate.step1.expiryDate'),
		content: cardInfo.expiryDate ? getMonthYear(cardInfo.expiryDate) : '-'
	}, {
		label: t('cards.activate.step1.availablePlafond'),
		content: !cardInfo.availablePlafond && cardInfo.availablePlafond !== 0 ? '-' : amountFormat(cardInfo.availablePlafond, cardInfo.currency)
	}, {
		label: t('cards.activate.step1.paymentMethodDescription'),
		content: cardInfo.paymentMethodDescription ? getMonthYear(cardInfo.paymentMethodDescription) : '-'
	}]
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	cardPanParams: cardPanParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

    if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

onMounted(() => {
    emit('scrollTo', 0)
})
</script>
